// Datas da agenda (ver AGENDA.md). A tela mostra sempre o horário da clínica, não o do navegador:
// a API devolve ISO 8601 já no fuso da agenda ("2026-10-19T09:00:00-03:00") e recebe horários
// sem fuso ("2026-10-19T09:00"), que o servidor interpreta no fuso da agenda. Por isso as contas
// aqui são feitas sobre datas "YYYY-MM-DD" e minutos do dia, sem converter para o fuso do navegador.

const pad = value => String(value).padStart(2, '0');

const toUTCDate = date => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const fromUTCDate = value =>
  `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;

// Data de hoje ("YYYY-MM-DD") no fuso da agenda.
export const todayIn = (timeZone, now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

// Minutos desde a meia-noite, agora, no fuso da agenda.
export const nowMinuteIn = (timeZone, now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const part = type => Number(parts.find(item => item.type === type).value);
  return part('hour') * 60 + part('minute');
};

// Até que minuto do dia o horário já passou: o dia todo em datas passadas, até agora hoje.
export const pastUntilMinute = (date, today, nowMinute) => {
  if (date < today) return 24 * 60;
  return date === today ? nowMinute : 0;
};

export const addDays = (date, days) => {
  const value = toUTCDate(date);
  value.setUTCDate(value.getUTCDate() + days);
  return fromUTCDate(value);
};

// 0 = domingo (mesma convenção da grade no servidor).
export const weekdayOf = date => toUTCDate(date).getUTCDay();

// Semana começando na segunda-feira.
export const startOfWeek = date => addDays(date, -((weekdayOf(date) + 6) % 7));

export const datePart = iso => iso.slice(0, 10);

export const timeToMinutes = time => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const minutesToTime = minutes =>
  `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

// Minutos desde a meia-noite de um ISO da API ("...T09:30:00-03:00" => 570).
export const minutesOfDay = iso => timeToMinutes(iso.slice(11, 16));

// Horário para a API, no fuso da agenda ("2026-10-19T09:30").
export const localDateTime = (date, minutes) =>
  `${date}T${minutesToTime(minutes)}`;

const formatDate = (date, locale, options) =>
  new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...options }).format(
    toUTCDate(date)
  );

// "segunda-feira, 19 de outubro"
export const formatDayLabel = (date, locale = 'pt-BR') =>
  formatDate(date, locale, { weekday: 'long', day: 'numeric', month: 'long' });

// "seg. 19"
export const formatShortDay = (date, locale = 'pt-BR') =>
  formatDate(date, locale, { weekday: 'short', day: 'numeric' });

// "19/10/2026"
export const formatNumericDate = (date, locale = 'pt-BR') =>
  formatDate(date, locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

// "19 a 25 de outubro de 2026" (ou "28 de setembro a 4 de outubro de 2026")
export const formatWeekLabel = (start, locale = 'pt-BR') => {
  const end = addDays(start, 6);
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  const first = sameMonth
    ? String(toUTCDate(start).getUTCDate())
    : formatDate(start, locale, { day: 'numeric', month: 'long' });
  const last = formatDate(end, locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `${first} a ${last}`;
};

// Colunas lado a lado para itens que se sobrepõem no mesmo dia (ex.: semana com todos os
// profissionais). Cada item recebe lane (posição) e lanes (total de colunas do seu grupo).
export const assignLanes = items => {
  const sorted = [...items].sort((a, b) => a.start - b.start || a.end - b.end);
  const result = [];
  let group = [];
  let laneEnds = [];
  let groupEnd = -1;

  const closeGroup = () => {
    group.forEach(item => result.push({ ...item, lanes: laneEnds.length }));
    group = [];
    laneEnds = [];
  };

  sorted.forEach(item => {
    if (item.start >= groupEnd) closeGroup();
    let lane = laneEnds.findIndex(end => end <= item.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.end);
    } else {
      laneEnds[lane] = item.end;
    }
    group.push({ ...item, lane });
    groupEnd = Math.max(groupEnd, item.end);
  });
  closeGroup();
  return result;
};

// Início do horário clicado: dentro de um período de atendimento, encaixa na grade de consultas
// (início do período + n × duração); fora dele, arredonda para 15 minutos (encaixe).
export const snapMinute = (minute, periods = [], duration = 30) => {
  const period = periods.find(
    item => minute >= item.start && minute < item.end
  );
  if (period) {
    const steps = Math.floor((minute - period.start) / duration);
    const lastStart = Math.max(period.start, period.end - duration);
    return Math.min(period.start + steps * duration, lastStart);
  }
  return Math.floor(minute / 15) * 15;
};
